import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Clock, User } from "@phosphor-icons/react";

/**
 * Zeigt Änderungsverlauf (Audit-Log) einer Entität.
 *
 * Props:
 *  - entityType: "tenancy" | "property"
 *  - entityId: string
 *  - title: Anzeigetitel (z. B. Mietvertragsnr. oder Objekt-ID)
 *  - open / onOpenChange: Dialog-Steuerung
 */
export default function ChangeHistoryDialog({ entityType, entityId, title, open, onOpenChange }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !entityId) return;
    setLoading(true);
    const url = entityType === "tenancy"
      ? `/tenancies/${entityId}/history`
      : `/properties/${entityId}/history`;
    api.get(url)
      .then((r) => setEntries(r.data || []))
      .catch(() => setEntries([]))
      .finally(() => setLoading(false));
  }, [open, entityId, entityType]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="change-history-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock size={18} /> Änderungsverlauf
          </DialogTitle>
          <DialogDescription>
            {title ? <>Für <span className="font-mono">{title}</span></> : "Wer hat wann was geändert"}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 text-center text-sm text-slate-500">Lade Verlauf…</div>
        ) : entries.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500">Keine Änderungen protokolliert.</div>
        ) : (
          <ol className="space-y-3" data-testid="history-list">
            {entries.map((e, idx) => {
              const diff = e.details?.diff || {};
              const fields = e.details?.changed_fields || Object.keys(diff);
              return (
                <li key={idx} className="border border-slate-200 rounded-md p-3 bg-white" data-testid={`history-entry-${idx}`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="flex items-center gap-2 text-sm">
                      <User size={14} className="text-slate-500" />
                      <span className="font-medium text-slate-900">{e.user_name || "—"}</span>
                      <span className="text-slate-400 text-xs">{e.user_email || ""}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono whitespace-nowrap">
                      {formatTs(e.created_at)}
                    </div>
                  </div>
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-indigo-700 mb-1">
                    {actionLabel(e.action)}
                  </div>
                  {fields.length > 0 ? (
                    <ul className="space-y-1 text-xs">
                      {fields.map((f) => (
                        <li key={f} className="font-mono bg-slate-50 border border-slate-100 rounded px-2 py-1">
                          <span className="text-slate-500">{fieldLabel(f)}:</span>{" "}
                          {renderDiffValue(diff[f])}
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-xs text-slate-500 italic">{e.details?.contract_number || e.details?.property_number || "—"}</div>
                  )}
                </li>
              );
            })}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

const ACTION_LABELS = {
  tenancy_created: "Mietverhältnis angelegt",
  tenancy_updated: "Mietverhältnis bearbeitet",
  tenancy_archived: "Mietverhältnis archiviert",
  property_created: "Objekt angelegt",
  property_updated: "Objekt bearbeitet",
  property_deleted: "Objekt gelöscht",
  posting_added: "Buchung hinzugefügt",
  soll_generated: "Soll generiert",
  rent_rule_added: "Mietregel hinzugefügt",
  rule_applied: "Mietregel angewendet",
};
function actionLabel(a) { return ACTION_LABELS[a] || a || "Aktion"; }

const FIELD_LABELS = {
  property_id: "Objekt", unit_id: "Einheit", additional_unit_ids: "Weitere Einheiten",
  tenancy_type: "Vertragsart", tenant_contact_ids: "Mieter",
  contract_start: "Vertragsbeginn", contract_end: "Vertragsende",
  termination_date: "Kündigungsdatum", handover_date: "Übergabe", handover_time: "Übergabe-Uhrzeit", return_date: "Rückgabe", return_time: "Rückgabe-Uhrzeit",
  rent_cold: "Kaltmiete", utilities_advance: "NK-Vorauszahlung",
  heating_advance: "Heizkosten-Vorauszahlung", parking_rent: "Stellplatzmiete",
  other_payments: "Sonstige Zahlungen", vat_rate: "MwSt.-Satz",
  deposit: "Kaution", deposit_status: "Kautionsstatus",
  payment_rhythm: "Zahlweise", due_day: "Fälligkeitstag",
  sepa_mandate: "SEPA-Mandat", rent_type: "Mietart",
  index_base: "Indexbasis", persons_count: "Personenanzahl",
  status: "Status", notes: "Notizen",
  property_number: "Objekt-ID", name: "Name", address: "Adresse",
  owner_contact_id: "Eigentümer", ownership_shares: "Eigentumsquoten",
  caretaker_user_id: "Betreuer", caretaker_contact_ids: "Hausmeister",
};
function fieldLabel(f) { return FIELD_LABELS[f] || f; }

function renderDiffValue(diff) {
  if (!diff) return null;
  const fmt = (v) => {
    if (v === null || v === undefined || v === "") return "—";
    if (Array.isArray(v)) return v.length === 0 ? "—" : `[${v.length}]`;
    if (typeof v === "object") return JSON.stringify(v).slice(0, 40);
    return String(v);
  };
  return (
    <span>
      <span className="text-rose-700 line-through">{fmt(diff.old)}</span>
      <span className="mx-1 text-slate-400">→</span>
      <span className="text-emerald-700 font-medium">{fmt(diff.new)}</span>
    </span>
  );
}
